import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search } from "lucide-react";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/admin/crm/whatsapp`;

const LEAD_STATI = ["Nuovo", "Contattato", "Perso", "Richiamare", "Contattare 24h", "Contattare 14gg", "Contattare 1 mese"];
const RELAZIONI = ["Figlio/a", "Coniuge", "Fratello/Sorella", "Nipote", "Genero/Nuora", "Amico/a", "Amministratore di sostegno", "Altro"];

const TARGETS = [
  { id: "struttura", label: "Struttura" },
  { id: "lead", label: "Lead" },
  { id: "ospite", label: "Ospite (come familiare)" },
];

const selectClass = "h-9 rounded-md border border-input bg-background px-2 text-sm";

export default function WhatsAppAssociaDialog({ open, onOpenChange, conversationKey, numero, headers, onDone }) {
  const [target, setTarget] = useState("struttura");
  const [filters, setFilters] = useState({});
  const [results, setResults] = useState([]);
  const [hint, setHint] = useState("");
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [familiareNome, setFamiliareNome] = useState("");
  const [relazione, setRelazione] = useState(RELAZIONI[0]);
  const [saving, setSaving] = useState(false);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (!open) return;
    setTarget("struttura"); setFilters({}); setResults([]); setPicked(null); setFamiliareNome(""); setRelazione(RELAZIONI[0]); setHint("");
  }, [open, conversationKey]);

  useEffect(() => { setFilters({}); setResults([]); setPicked(null); setHint(""); }, [target]);

  useEffect(() => {
    if (!open) return undefined;
    const path = { struttura: "strutture", lead: "lead", ospite: "ospiti" }[target];
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value !== "" && value != null && value !== false) params.set(key, String(value)); });
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${BASE}/cerca/${path}?${params.toString()}`, { headers });
        const json = await res.json().catch(() => ({}));
        if (res.status === 400) { setResults([]); setHint(json.detail || ""); return; }
        if (!res.ok) throw new Error(json.detail || "Ricerca non riuscita");
        setHint("");
        setResults(json.data || []);
      } catch (error) { toast.error(error.message); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target, filters]);

  const save = async () => {
    if (!picked) return;
    if (target === "ospite" && !familiareNome.trim()) return toast.error("Scrivi il nome del familiare che ha scritto.");
    setSaving(true);
    try {
      const body = target === "struttura" ? { struttura_id: picked.id }
        : target === "lead" ? { lead_id: picked.id }
          : { fascicolo_id: picked.id, familiare_nome: familiareNome.trim(), familiare_relazione: relazione };
      const res = await fetch(`${BASE}/conversazioni/${encodeURIComponent(conversationKey)}/associa`, { method: "POST", headers, body: JSON.stringify(body) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Associazione non riuscita");
      toast.success(json.familiare_aggiunto ? "Numero aggiunto ai familiari dell'ospite" : "Conversazione associata");
      if (json.proposta_creata) toast.info("La risposta è stata letta: la trovi in «Da confermare».");
      onOpenChange(false);
      onDone?.(json);
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const label = (text, children) => <div className="space-y-1"><Label className="text-xs text-slate-500">{text}</Label>{children}</div>;

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Associa il numero {numero}</DialogTitle>
        <DialogDescription>Scegli a chi appartiene questo numero. Cercalo con i filtri e selezionalo dall'elenco.</DialogDescription>
      </DialogHeader>
      <div className="flex flex-wrap gap-1">{TARGETS.map((item) => <Button key={item.id} size="sm" variant={target === item.id ? "default" : "outline"} onClick={() => setTarget(item.id)}>{item.label}</Button>)}</div>

      <div className="grid gap-2 sm:grid-cols-3">
        {target === "struttura" && <>
          {label("Nome / referente", <Input value={filters.q || ""} onChange={(e) => setFilter("q", e.target.value)} placeholder="Es. Aurora" />)}
          {label("Comune", <Input value={filters.comune || ""} onChange={(e) => setFilter("comune", e.target.value)} />)}
          {label("Provincia", <Input value={filters.provincia || ""} onChange={(e) => setFilter("provincia", e.target.value)} placeholder="Es. RM" />)}
          <label className="flex items-center gap-2 text-sm sm:col-span-3"><input type="checkbox" checked={filters.stato === "tutte"} onChange={(e) => setFilter("stato", e.target.checked ? "tutte" : "")} /> Includi le strutture non attive</label>
        </>}
        {target === "lead" && <>
          {label("Nome, telefono o email", <Input value={filters.q || ""} onChange={(e) => setFilter("q", e.target.value)} placeholder="Es. Mario Rossi" />)}
          {label("Città", <Input value={filters.citta || ""} onChange={(e) => setFilter("citta", e.target.value)} />)}
          {label("Stato lead", <select className={selectClass} value={filters.stato || ""} onChange={(e) => setFilter("stato", e.target.value)}><option value="">Tutti</option>{LEAD_STATI.map((s) => <option key={s} value={s}>{s}</option>)}</select>)}
        </>}
        {target === "ospite" && <>
          {label("Nome o cognome dell'ospite", <Input value={filters.q || ""} onChange={(e) => setFilter("q", e.target.value)} placeholder="Es. Anna Verdi" />)}
          {label("Struttura", <Input value={filters.struttura || ""} onChange={(e) => setFilter("struttura", e.target.value)} placeholder="Nome della struttura" />)}
          <label className="flex items-end gap-2 pb-2 text-sm"><input type="checkbox" checked={!!filters.archiviati} onChange={(e) => setFilter("archiviati", e.target.checked)} /> Includi archiviati</label>
        </>}
      </div>

      <div className="max-h-64 space-y-1 overflow-y-auto rounded border p-1">
        {searching && <p className="p-2 text-sm text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Ricerca…</p>}
        {!searching && hint && <p className="p-2 text-sm text-slate-500"><Search className="mr-2 inline h-4 w-4" />{hint}</p>}
        {!searching && !hint && !results.length && <p className="p-2 text-sm text-slate-500">Nessun risultato con questi filtri.</p>}
        {results.map((item) => <button key={item.id} type="button" onClick={() => setPicked(item)} className={`w-full rounded p-2 text-left text-sm ${picked?.id === item.id ? "bg-emerald-100" : "hover:bg-slate-50"}`}>
          {target === "struttura" && <><strong>{item.nome}</strong><p className="text-xs text-slate-500">{[item.comune, item.provincia].filter(Boolean).join(" · ") || "Comune non indicato"}{item.attivo ? "" : " · non attiva"}{item.contatti?.length ? ` · ${item.contatti.map((c) => c.telefono).join(", ")}` : ""}</p></>}
          {target === "lead" && <><strong>{item.nome}</strong><p className="text-xs text-slate-500">{[item.ospite && `Ospite: ${item.ospite}`, item.citta, item.stato, item.telefono].filter(Boolean).join(" · ")}</p></>}
          {target === "ospite" && <><strong>{item.nome}</strong><p className="text-xs text-slate-500">{[item.struttura, item.camera && `Camera ${item.camera}`, item.archiviata && "archiviato"].filter(Boolean).join(" · ")}{item.familiari?.length ? ` · Familiari: ${item.familiari.map((f) => `${f.nome}${f.relazione ? ` (${f.relazione})` : ""}`).join(", ")}` : " · Nessun familiare inserito"}</p></>}
        </button>)}
      </div>

      {picked && target === "ospite" && <div className="space-y-2 rounded border border-emerald-200 bg-emerald-50 p-3">
        <p className="text-sm">Il numero <strong>{numero}</strong> verrà aggiunto tra i familiari di <strong>{picked.nome}</strong>.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {label("Nome del familiare", <Input value={familiareNome} onChange={(e) => setFamiliareNome(e.target.value)} placeholder="Es. Luca Verdi" />)}
          {label("Relazione con l'ospite", <select className={selectClass} value={relazione} onChange={(e) => setRelazione(e.target.value)}>{RELAZIONI.map((r) => <option key={r} value={r}>{r}</option>)}</select>)}
        </div>
      </div>}
      {picked && target !== "ospite" && <p className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm">Il numero <strong>{numero}</strong> verrà collegato a <strong>{picked.nome}</strong>.{target === "struttura" ? " Se c'è una risposta recente, l'IA la leggerà e la troverai in «Da confermare»." : ""}</p>}

      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={save} disabled={!picked || saving}>{saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}{target === "ospite" ? "Aggiungi come familiare e associa" : "Associa"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}
